import React, { useState, useEffect } from 'react';
import dayjs from "dayjs";
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  CardHeader,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  LinearProgress,
  Chip,
  Avatar,
  Divider,
  IconButton,
  useTheme,
  useMediaQuery,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Snackbar,
  Tooltip,
  Stack,
  CardActions
} from '@mui/material';
import {
  People as PeopleIcon,
  School as SchoolIcon,
  AttachMoney as MoneyIcon,
  CalendarToday as CalendarIcon,
  Cake as CakeIcon,
  EventBusy as LeaveIcon,
  TrendingUp as TrendingUpIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  TableChart as TableChartIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Visibility as VisibilityIcon,
  Numbers as NumbersIcon,
  AccountBalance as AccountBalanceIcon,
  Group as GroupIcon,
  CurrencyRupee
} from '@mui/icons-material';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip as RechartsTooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, AreaChart, Area } from 'recharts';
import authService from '../Auth/AuthService';

const AdminDashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Loading states
  const [loading, setLoading] = useState({
    dashboard: true,
    studentSummary: false,
    feeSummary: false,
    topStudents: false,
    birthdays: false,
    employeeSummary: false,
    employeeLeaves: false
  });

  // Data states
  const [studentSummary, setStudentSummary] = useState({});
  const [employeeSummary, setEmployeeSummary] = useState({});
  const [feeSummary, setFeeSummary] = useState([]);
  const [topStudentsByMarks, setTopStudentsByMarks] = useState([]);
  const [topStudentsByFees, setTopStudentsByFees] = useState([]);
  const [todayBirthdays, setTodayBirthdays] = useState([]);
  const [employeeLeaves, setEmployeeLeaves] = useState([]);
  // const [firms, setFirms] = useState([]);
  const [feesData, setFeesData] = useState([]);

  // Filter states
  // const [selectedBranch, setSelectedBranch] = useState('');
  const [selectedFirm, setSelectedFirm] = useState('');
  const [todayEmployeeBirthdays, setTodayEmployeeBirthdays] = useState('');
  const [selectedDate, setSelectedDate] = useState(dayjs().format('YYYY-MM-DD'));
  // const [createdBy, setCreatedBy] = useState('admin'); // Should come from auth context
  // const [academicYear, setAcademicYear] = useState('');
  const [topStudentsView, setTopStudentsView] = useState('marks'); // marks or fees

  const [tabValue, setTabValue] = useState(0);
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  // const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658', '#8dd1e1'];

  // Initialize data on component mount
  useEffect(() => {
    fetchAllData();
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    if (selectedDate) {
      fetchAllData();
    }
  }, [selectedDate, selectedFirm]);

  const fetchAllData = async () => {
    setLoading(prev => ({ ...prev, dashboard: true }));
    try {
      await Promise.all([
        fetchStudentSummary(),
        fetchEmployeeSummary(),
        fetchFeeSummary(),
        fetchTopStudents('marks'),
        fetchTopStudents('fees'),
        fetchTodayBirthdays(),
        fetchEmployeeLeaves(),
        fetchSessionChart(),
      ]);
    } catch (error) {
      console.error('Error fetching all data:', error);
      showSnackbar('Error loading dashboard data', 'error');
    } finally {
      setLoading(prev => ({ ...prev, dashboard: false }));
    }
  };

  // Student Summary Endpoint
  const fetchStudentSummary = async () => {
    setLoading(prev => ({ ...prev, studentSummary: true }));
    try {
      const response = await authService.getAuth(authService.pathUrl("GET", "/student/dashboard/summary?date=" + selectedDate));
      setStudentSummary(response);
    } catch (error) {
      console.error('Error fetching student summary:', error);
      showSnackbar('Error loading student summary', 'error');
    } finally {
      setLoading(prev => ({ ...prev, studentSummary: false }));
    }
  };

  // Employee Summary Endpoint
  const fetchEmployeeSummary = async () => {
    setLoading(prev => ({ ...prev, employeeSummary: true }));
    try {
      const response = await authService.getAuth(authService.pathUrl("GET", "/employee/dashboard/employee-summary?date=" + selectedDate));
      setEmployeeSummary(response);
    } catch (error) {
      console.error('Error fetching employee summary:', error);
      showSnackbar('Error loading employee summary', 'error');
    } finally {
      setLoading(prev => ({ ...prev, employeeSummary: false }));
    }
  };

  // Fee Summary Endpoint
  const fetchFeeSummary = async () => {
    setLoading(prev => ({ ...prev, feeSummary: true }));
    try {
      const response = await authService.getAuth(authService.pathUrl("GET", "/student/dashboard/fee-summary?date=" + selectedDate));
      setFeeSummary(response);
    } catch (error) {
      console.error('Error fetching fee summary:', error);
      showSnackbar('Error loading fee summary', 'error');
    } finally {
      setLoading(prev => ({ ...prev, feeSummary: false }));
    }
  };

  // Top Students Endpoints
  const fetchTopStudents = async (type = 'marks') => {
    setLoading(prev => ({ ...prev, topStudents: true }));
    try {
      let endpoint = '';

      const topstud = selectedDate + "&criteria=" + type
      if (type === 'marks') {
        endpoint = authService.pathUrl("GET", "/student/dashboard/top-students/marks?date=" + selectedDate)
      } else if (type === 'fees') {
        endpoint = authService.pathUrl("GET", "/student/dashboard/top-students/fees?date=" + selectedDate)
      } else {
        endpoint = authService.pathUrl("GET", "/student/dashboard/top-students?date=" + topstud)
      }

      const response = await authService.getAuth(endpoint);

      if (type === 'marks') {
        setTopStudentsByMarks(response);
      } else if (type === 'fees') {
        setTopStudentsByFees(response);
      }
    } catch (error) {
      console.error(`Error fetching top students (${type}):`, error);
      showSnackbar(`Error loading top students by ${type}`, 'error');
    } finally {
      setLoading(prev => ({ ...prev, topStudents: false }));
    }
  };

  // Today's Birthdays Endpoint
  const fetchTodayBirthdays = async () => {
    setLoading(prev => ({ ...prev, birthdays: true }));
    try {
      const response = await authService.getAuth(authService.pathUrl("GET", "/student/dashboard/today-birthdays?date=" + selectedDate));
      setTodayBirthdays(response);
    } catch (error) {
      console.error('Error fetching birthdays:', error);
      showSnackbar('Error loading birthdays', 'error');
    } finally {
      setLoading(prev => ({ ...prev, birthdays: false }));
    }
  };
  const fetchTodayEmployeeBirthdays = async () => {
    setLoading(prev => ({ ...prev, birthdays: true }));
    try {
      const response = await authService.getAuth(authService.pathUrl("GET", "/employee/dashboard/emp-today-birthdays?date=" + selectedDate));
      setTodayEmployeeBirthdays(response);
    } catch (error) {
      console.error('Error fetching birthdays:', error);
      showSnackbar('Error loading birthdays', 'error');
    } finally {
      setLoading(prev => ({ ...prev, birthdays: false }));
    }
  };

  // Employee Leaves Endpoint
  const fetchEmployeeLeaves = async () => {
    setLoading(prev => ({ ...prev, employeeLeaves: true }));
    try {
      const response = await authService.getAuth(authService.pathUrl("GET", "/employee/dashboard/employee-leaves?date=" + selectedDate));
      setEmployeeLeaves(response);
    } catch (error) {
      console.error('Error fetching employee leaves:', error);
      showSnackbar('Error loading employee leaves', 'error');
    } finally {
      setLoading(prev => ({ ...prev, employeeLeaves: false }));
    }
  };

  // Firms Endpoint
  // const fetchFirms = async () => {
  //   try {
  //     const response = await axios.get(`${API_URL}/dashboard/firms`, {
  //       params: { createdBy: createdBy }
  //     });
  //     setFirms(response.data);
  //   } catch (error) {
  //     console.error('Error fetching firms:', error);
  //   }
  // };

  const fetchSessionChart = async () => {
    try {
      const response = await authService.getAuth(authService.pathUrl("GET", "/student/dashboard/chart/monthly?date=" + selectedDate));
      setFeesData(response);
    } catch (error) {
      console.error('Error fetching Fees Data:', error);
    }
  };

  const handleFirmChange = (event) => {
    const value = event.target.value;
    setSelectedFirm(value);
    // fetchDeliveryPersons(value);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num || 0);
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = () => {
    showSnackbar('Export feature coming soon', 'info');
  };

  const handleRefreshAll = () => {
    fetchAllData();
    showSnackbar('Refreshing all data...', 'info');
  };

  const handleRefreshSection = async (section) => {
    switch (section) {
      case 'student':
        await fetchStudentSummary();
        break;
      case 'employee':
        await fetchEmployeeSummary();
        break;
      case 'fees':
        await fetchFeeSummary();
        break;
      case 'topStudents':
        await fetchTopStudents(topStudentsView);
        break;
      case 'birthdays':
        await fetchTodayBirthdays();
        break;
      case 'emp-birthdays':
        await fetchTodayEmployeeBirthdays();
        break;
      case 'session-chart':
        await fetchSessionChart();
        break;
      case 'leaves':
        await fetchEmployeeLeaves();
        break;
    }
    showSnackbar(`${section} data refreshed`, 'success');
  };

  const StatCard = ({ title, value, icon, color, subtext, prefix = '', suffix = '', loading: cardLoading }) => (
    <Card sx={{
      height: '100%',
      position: 'relative',
      overflow: 'visible',
      transition: 'transform 0.3s ease-in-out',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: theme.shadows[8]
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Box sx={{
            color,
            bgcolor: `${color}15`,
            p: 1,
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {icon}
          </Box>
        </Box>

        {cardLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={30} />
          </Box>
        ) : (
          <>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
              {prefix}{formatNumber(value)}{suffix}
            </Typography>
            {subtext && (
              <Typography variant="caption" color="text.secondary">
                {subtext}
              </Typography>
            )}
          </>
        )}
      </CardContent>
      <CardActions sx={{ pt: 0, justifyContent: 'flex-end' }}>
        <IconButton size="small" onClick={() => handleRefreshSection(title?.toLowerCase())}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </CardActions>
    </Card>
  );

  const FeeCard = ({ title, amount, icon, loading: cardLoading }) => (
    <Card sx={{
      height: '100%',
      transition: 'transform 0.3s ease-in-out',
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: theme.shadows[6]
      }
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography color="text.secondary" variant="caption" gutterBottom>
              {title}
            </Typography>
            {cardLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {formatCurrency(amount)}
              </Typography>
            )}
          </Box>
          <Box sx={{ color: '#4caf50', bgcolor: '#e8f5e9', p: 1.2, borderRadius: '10px' }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  // Calculate percentages for charts
  const getAttendanceData = () => {
    const studentPresent = studentSummary?.totalStudent ?
      (studentSummary?.totalStudent || 0) - (studentSummary?.todayAbsentstd || 0) : 0;
    const studentTotal = studentSummary?.totalStudent || 0;

    return [
      {
        name: 'Students',
        present: studentPresent,
        absent: studentSummary?.todayAbsentstd || 0,
        presentPercent: studentTotal > 0 ? (studentPresent / studentTotal * 100)?.toFixed(1) : 0,
        absentPercent: studentTotal > 0 ? (studentSummary?.todayAbsentstd / studentTotal * 100)?.toFixed(1) || 0 : 0
      },
      {
        name: 'Employees',
        present: employeeSummary?.totalPresentEmp || 0,
        absent: employeeSummary?.totalAbsentEmp || 0,
        presentPercent: employeeSummary?.totalEmployee > 0 ?
          (employeeSummary?.totalPresentEmp / employeeSummary?.totalEmployee * 100)?.toFixed(1) : 0,
        absentPercent: employeeSummary?.totalEmployee > 0 ?
          (employeeSummary?.totalAbsentEmp / employeeSummary?.totalEmployee * 100)?.toFixed(1) : 0
      }
    ];
  };

  const getFeePaymentData = () => {
    const totalStudents = studentSummary?.totalStudent || 0;
    const paidStudents = studentSummary?.totalPaid || 0;
    const unpaidStudents = totalStudents - paidStudents;

    return [
      { name: 'Paid', value: paidStudents, color: '#4caf50' },
      { name: 'Unpaid', value: unpaidStudents, color: '#f44336' }
    ];
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              Dashboard
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Real-time insights and analytics for school administration
            </Typography>
          </Box>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh All Data">
              <IconButton onClick={handleRefreshAll} color="primary" disabled={loading.dashboard}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Data">
              <IconButton onClick={handleExport} color="primary">
                <DownloadIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Print Dashboard">
              <IconButton onClick={handlePrint} color="primary">
                <PrintIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<BarChartIcon />}
              onClick={() => setOpenReportDialog(true)}
            >
              Generate Report
            </Button>
          </Stack>
        </Box>

        {/* Filters */}
        <Card sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            {/* <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Branch</InputLabel>
                <Select
                  value={selectedBranch}
                  label="Select Branch"
                  onChange={(e) => setSelectedBranch(e.target.value)}
                  disabled={loading.dashboard}
                >
                  <MenuItem value="">All Branches</MenuItem>
                  <MenuItem value="1">Main Branch</MenuItem>
                  <MenuItem value="2">Branch 2</MenuItem>
                </Select>
              </FormControl>
            </Grid> */}

            {/* <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Firm</InputLabel>
                <Select
                  value={selectedFirm}
                  label="Select Firm"
                  onChange={handleFirmChange}
                  disabled={loading.dashboard}
                >
                  <MenuItem value="">All Firms</MenuItem>
                  {firms.map((firm) => (
                    <MenuItem key={firm.id} value={firm.id}>
                      {firm.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}

            {/* <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Select Delivery Person</InputLabel>
                <Select
                  value={selectedDeliveryPerson}
                  label="Select Delivery Person"
                  onChange={(e) => setSelectedDeliveryPerson(e.target.value)}
                  disabled={!selectedFirm || loading.dashboard}
                >
                  <MenuItem value="">All Delivery Persons</MenuItem>
                  {deliveryPersons.map((person) => (
                    <MenuItem key={person.id} value={person.id}>
                      {person.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid> */}

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Select Date"
                size="small"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                disabled={loading.dashboard}
              />
            </Grid>

            {/* <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Academic Year"
                size="small"
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
                placeholder="e.g., 2023-2024"
                disabled={loading.dashboard}
              />
            </Grid> */}

            {/* <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Created By</InputLabel>
                <Select
                  value={createdBy}
                  label="Created By"
                  onChange={(e) => setCreatedBy(e.target.value)}
                  disabled={loading.dashboard}
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="teacher">Teacher</MenuItem>
                  <MenuItem value="accountant">Accountant</MenuItem>
                </Select>
              </FormControl>
            </Grid> */}
          </Grid>
        </Card>
      </Box>

      {loading.dashboard && <LinearProgress sx={{ mb: 2 }} />}

      {/* Tabs for different views */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={(e, newValue) => setTabValue(newValue)}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
        >
          <Tab label="Overview" icon={<BarChartIcon />} iconPosition="start" />
          <Tab label="Students" icon={<SchoolIcon />} iconPosition="start" />
          <Tab label="Employees" icon={<PeopleIcon />} iconPosition="start" />
          <Tab label="Fees" icon={<CurrencyRupee />} iconPosition="start" />
          <Tab label="Analytics" icon={<TrendingUpIcon />} iconPosition="start" />
        </Tabs>
      </Box>

      {/* Overview Tab */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          {/* Student Statistics */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2, mb: 3 }}>
              <CardHeader
                title="Student Statistics"
                avatar={<SchoolIcon color="primary" />}
                action={
                  <IconButton
                    onClick={() => handleRefreshSection('student')}
                    disabled={loading.studentSummary}
                  >
                    <RefreshIcon />
                  </IconButton>
                }
              />
              <Divider />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <StatCard
                      title="Total Students"
                      value={studentSummary?.totalStudent || 0}
                      icon={<GroupIcon />}
                      color="#2196f3"
                      loading={loading.studentSummary}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <StatCard
                      title="NON-RTE Students"
                      value={studentSummary?.totalStudentNonRte || 0}
                      icon={<NumbersIcon />}
                      color="#ff9800"
                      loading={loading.studentSummary}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <StatCard
                      title="RTE Students"
                      value={studentSummary?.totalStudentRte || 0}
                      icon={<NumbersIcon />}
                      color="#9c27b0"
                      loading={loading.studentSummary}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <StatCard
                      title="Students Present"
                      value={(studentSummary?.totalStudent || 0) - (studentSummary?.todayAbsentstd || 0)}
                      icon={<CheckCircleIcon sx={{ color: '#4caf50' }} />}
                      color="#4caf50"
                      loading={loading.studentSummary}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <StatCard
                      title="Students Absent"
                      value={studentSummary?.todayAbsentstd || 0}
                      icon={<CancelIcon sx={{ color: '#f44336' }} />}
                      color="#f44336"
                      loading={loading.studentSummary}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <StatCard
                      title="Paid Students"
                      value={studentSummary?.totalPaid || 0}
                      icon={<AccountBalanceIcon />}
                      color="#4caf50"
                      loading={loading.studentSummary}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <StatCard
                      title="Unpaid Students"
                      value={(studentSummary?.totalStudent || 0) - (studentSummary?.totalPaid || 0)}
                      icon={<AccountBalanceIcon />}
                      color="#f44336"
                      loading={loading.studentSummary}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Employee Statistics */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2, mb: 3 }}>
              <CardHeader
                title="Employee Statistics"
                avatar={<PeopleIcon color="secondary" />}
                action={
                  <IconButton
                    onClick={() => handleRefreshSection('employee')}
                    disabled={loading.employeeSummary}
                  >
                    <RefreshIcon />
                  </IconButton>
                }
              />
              <Divider />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <StatCard
                      title="Total Employees"
                      value={employeeSummary?.totalEmployee || 0}
                      icon={<PeopleIcon />}
                      color="#3f51b5"
                      loading={loading.employeeSummary}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <StatCard
                      title="Present Employees"
                      value={employeeSummary?.totalPresentEmp || 0}
                      icon={<PersonIcon sx={{ color: '#4caf50' }} />}
                      color="#4caf50"
                      subtext={employeeSummary?.totalEmployee > 0 ?
                        `${((employeeSummary?.totalPresentEmp || 0) / employeeSummary?.totalEmployee * 100)?.toFixed(1)}% attendance` :
                        ''}
                      loading={loading.employeeSummary}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3}>
                    <StatCard
                      title="Absent Employees"
                      value={employeeSummary?.totalAbsentEmp || 0}
                      icon={<PersonIcon sx={{ color: '#f44336' }} />}
                      color="#f44336"
                      loading={loading.employeeSummary}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Charts Section */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <CardHeader
                title="Attendance Overview"
                avatar={<PersonIcon color="primary" />}
              />
              <Divider />
              <CardContent sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={getAttendanceData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip
                      formatter={(value, name) => [formatNumber(value), name]}
                      labelFormatter={(label) => `${label}`}
                    />
                    <Legend />
                    <Bar dataKey="present" fill="#4caf50" name="Present" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="absent" fill="#f44336" name="Absent" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <CardHeader
                title="Fee Payment Status"
                avatar={<CurrencyRupee color="secondary" />}
              />
              <Divider />
              <CardContent sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={getFeePaymentData()}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100)?.toFixed(1)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {getFeePaymentData()?.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => [formatNumber(value), 'Count']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Students Tab */}
      {tabValue === 1 && (
        <Grid container spacing={3}>
          {/* Top Students Section */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2, mb: 3 }}>
              <CardHeader
                title={`Top Students`}
                avatar={<TrendingUpIcon color="primary" />}
                action={
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                      <Select
                        value={topStudentsView}
                        onChange={(e) => {
                          setTopStudentsView(e.target.value);
                          fetchTopStudents(e.target.value);
                        }}
                      >
                        <MenuItem value="marks">By Marks</MenuItem>
                        <MenuItem value="fees">By Fees</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton
                      onClick={() => handleRefreshSection('topStudents')}
                      disabled={loading.topStudents}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Stack>
                }
              />
              <Divider />
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Rank</TableCell>
                      <TableCell>Student Name</TableCell>
                      <TableCell>Student Code</TableCell>
                      <TableCell>Class</TableCell>
                      {topStudentsView === 'marks' ? (
                        <>
                          <TableCell align="right">Percentage</TableCell>
                          <TableCell align="right">Grade</TableCell>
                          <TableCell align="right">Exam Type</TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell align="right">Fees Paid</TableCell>
                          <TableCell align="right">% of Total</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading.topStudents ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <CircularProgress />
                          <Typography sx={{ mt: 1 }}>Loading top students...</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (topStudentsView === 'marks' ? topStudentsByMarks : topStudentsByFees)?.map((student, index) => (
                      <TableRow key={index} hover>
                        <TableCell>
                          <Chip
                            label={`#${index + 1}`}
                            size="small"
                            color={index < 3 ? "primary" : "default"}
                            variant={index < 3 ? "filled" : "outlined"}
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Avatar sx={{
                              width: 32,
                              height: 32,
                              bgcolor: COLORS[index % COLORS.length],
                              fontSize: '0.875rem'
                            }}>
                              {student.name?.charAt(0) || 'S'}
                            </Avatar>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {student.name}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {student.studentCode}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${student.standard || 'N/A'} ${student.section || ''}`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        {topStudentsView === 'marks' ? (
                          <>
                            <TableCell align="right">
                              <Box sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.5,
                                color: (student.percentage || 0) >= 75 ? '#4caf50' :
                                  (student.percentage || 0) >= 60 ? '#ff9800' : '#f44336'
                              }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {student.percentage?.toFixed(1) || 0}%
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={student.grade || 'N/A'}
                                size="small"
                                sx={{
                                  bgcolor: student.grade === 'A+' ? '#4caf50' :
                                    student.grade === 'A' ? '#8bc34a' :
                                      student.grade === 'B+' ? '#ff9800' : '#ffc107',
                                  color: 'white'
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={student.examType || 'N/A'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell align="right">
                              <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                {formatCurrency(student.count * 10000 || 0)}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <LinearProgress
                                  variant="determinate"
                                  value={student.percent || 0}
                                  sx={{
                                    width: 60,
                                    height: 6,
                                    borderRadius: 3,
                                    bgcolor: 'grey.200'
                                  }}
                                />
                                <Typography variant="body2">
                                  {(student.percent || 0)?.toFixed(1)}%
                                </Typography>
                              </Box>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* Birthday & Fee Summary */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ borderRadius: 2, mb: 3 }}>
              <CardHeader
                title="Today's Birthdays"
                avatar={<CakeIcon color="secondary" />}
                action={
                  <IconButton
                    onClick={() => handleRefreshSection('birthdays')}
                    disabled={loading.birthdays}
                    size="small"
                  >
                    <RefreshIcon />
                  </IconButton>
                }
              />
              <Divider />
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {loading.birthdays ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 2 }}>
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    ) : todayBirthdays?.length > 0 ? (
                      todayBirthdays?.slice(0, 5)?.map((student, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {student.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Class {student.standard}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${student.ages} Year${student.ages > 1 ? 's' : ''}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          <Typography color="text.secondary">No birthdays today</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          <Grid item xs={12} lg={8}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader
                title="Fee Summary"
                avatar={<CurrencyRupee color="primary" />}
                action={
                  <IconButton
                    onClick={() => handleRefreshSection('fees')}
                    disabled={loading.feeSummary}
                  >
                    <RefreshIcon />
                  </IconButton>
                }
              />
              <Divider />
              <CardContent>
                <Grid container spacing={2}>
                  {loading.feeSummary ? (
                    <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                      <Typography sx={{ mt: 1 }}>Loading fee summary...</Typography>
                    </Grid>
                  ) : feeSummary?.map((fee, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <FeeCard
                        title={fee.title}
                        amount={fee.amount}
                        icon={<CurrencyRupee />}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Employees Tab */}
      {tabValue === 2 && (
        <Grid container spacing={3}>
          {/* Employee Summary */}
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader
                title="Employee Summary"
                avatar={<PeopleIcon color="primary" />}
                action={
                  <IconButton
                    onClick={() => handleRefreshSection('employee')}
                    disabled={loading.employeeSummary}
                  >
                    <RefreshIcon />
                  </IconButton>
                }
              />
              <Divider />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                      title="Total Employees"
                      value={employeeSummary?.totalEmployee || 0}
                      icon={<PeopleIcon />}
                      color="#3f51b5"
                      loading={loading.employeeSummary}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                      title="Present Today"
                      value={employeeSummary?.totalPresentEmp || 0}
                      icon={<CheckCircleIcon sx={{ color: '#4caf50' }} />}
                      color="#4caf50"
                      loading={loading.employeeSummary}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                      title="Absent Today"
                      value={employeeSummary?.totalAbsentEmp || 0}
                      icon={<CancelIcon sx={{ color: '#f44336' }} />}
                      color="#f44336"
                      loading={loading.employeeSummary}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Employee Leaves */}
          <Grid item xs={12} md={4}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader
                title="Today's Employee Leaves"
                avatar={<LeaveIcon color="error" />}
                action={
                  <IconButton
                    onClick={() => handleRefreshSection('leaves')}
                    disabled={loading.employeeLeaves}
                    size="small"
                  >
                    <RefreshIcon />
                  </IconButton>
                }
              />
              <Divider />
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {loading.employeeLeaves ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 2 }}>
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    ) : employeeLeaves?.length > 0 ? (
                      employeeLeaves.slice(0, 5).map((employee, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{
                                width: 32,
                                height: 32,
                                bgcolor: '#f44336',
                                fontSize: '0.875rem'
                              }}>
                                {employee.name?.charAt(0) || 'E'}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                  {employee.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {employee.mobileno}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={employee.leave}
                              size="small"
                              color="error"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          <Typography color="text.secondary">No leaves today</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* employee's Birthday & Fee Summary */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ borderRadius: 2, mb: 3 }}>
              <CardHeader
                title="Today's Birthdays"
                avatar={<CakeIcon color="secondary" />}
                action={
                  <IconButton
                    onClick={() => handleRefreshSection('emp-birthdays')}
                    disabled={loading.birthdays}
                    size="small"
                  >
                    <RefreshIcon />
                  </IconButton>
                }
              />
              <Divider />
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {loading.birthdays ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ py: 2 }}>
                          <CircularProgress size={20} />
                        </TableCell>
                      </TableRow>
                    ) : todayBirthdays?.length > 0 ? (
                      todayEmployeeBirthdays?.slice(0, 5)?.map((emp, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                {emp.name}
                              </Typography>
                              {/* <Typography variant="caption" color="text.secondary">
                                Class {student.standard}
                              </Typography> */}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={`${emp.ages} Year${emp.ages > 1 ? 's' : ''}`}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          <Typography color="text.secondary">No birthdays today</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Fees Tab */}
      {tabValue === 3 && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2, mb: 3 }}>
              <CardHeader
                title="Fee Collection Summary"
                avatar={<CurrencyRupee color="primary" />}
                action={
                  <IconButton
                    onClick={() => handleRefreshSection('fees')}
                    disabled={loading.feeSummary}
                  >
                    <RefreshIcon />
                  </IconButton>
                }
              />
              <Divider />
              <CardContent>
                <Grid container spacing={3}>
                  {loading.feeSummary ? (
                    <Grid item xs={12} sx={{ textAlign: 'center', py: 4 }}>
                      <CircularProgress />
                      <Typography sx={{ mt: 1 }}>Loading fee data...</Typography>
                    </Grid>
                  ) : feeSummary?.map((fee, index) => (
                    <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                      <FeeCard
                        title={fee.title}
                        amount={fee.amount}
                        icon={<CurrencyRupee />}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Fee Trends Chart */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader
                title="Fee Collection Trends"
                avatar={<BarChartIcon color="secondary" />}
              />
              <Divider />
              <CardContent sx={{ height: 400 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    // data={[
                    //   { month: 'Jan', amount: 150000 },
                    //   { month: 'Feb', amount: 180000 },
                    //   { month: 'Mar', amount: 220000 },
                    //   { month: 'Apr', amount: 190000 },
                    //   { month: 'May', amount: 250000 },
                    //   { month: 'Jun', amount: 300000 },
                    // ]}
                    data={feesData}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(value) => `₹${value / 1000}K`} />
                    <RechartsTooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                    <Area type="monotone" dataKey="amount" stroke="#8884d8" fill="#8884d8" fillOpacity={0.3} />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Analytics Tab */}
      {tabValue === 4 && (
        <Grid container spacing={3}>
          {/* Attendance Analytics */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <CardHeader
                title="Attendance Analytics"
                avatar={<PersonIcon color="primary" />}
              />
              <Divider />
              <CardContent sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={getAttendanceData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <RechartsTooltip
                      formatter={(value, name) => [formatNumber(value), name]}
                      labelFormatter={(label) => `${label}`}
                    />
                    <Legend />
                    <Bar dataKey="present" fill="#4caf50" name="Present" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="absent" fill="#f44336" name="Absent" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Student Distribution */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ borderRadius: 2, height: '100%' }}>
              <CardHeader
                title="Student Distribution"
                avatar={<SchoolIcon color="secondary" />}
              />
              <Divider />
              <CardContent sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'NON-RTE', value: studentSummary?.totalStudent || 0, color: '#ff9800' },
                        { name: 'RTE', value: studentSummary?.totalStudentRte || 0, color: '#9c27b0' }
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      <Cell fill="#ff9800" />
                      <Cell fill="#9c27b0" />
                    </Pie>
                    <RechartsTooltip formatter={(value) => [formatNumber(value), 'Count']} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Performance Metrics */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 2 }}>
              <CardHeader
                title="Performance Metrics"
                avatar={<TrendingUpIcon color="primary" />}
              />
              <Divider />
              <CardContent>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      title="Attendance Rate"
                      value={studentSummary?.totalStudent > 0 ?
                        (((studentSummary?.totalStudent || 0) - (studentSummary?.todayAbsentstd || 0)) / studentSummary?.totalStudent * 100)?.toFixed(1) : 0}
                      icon={<PersonIcon />}
                      color="#4caf50"
                      suffix="%"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      title="Fee Collection Rate"
                      value={studentSummary?.totalStudent > 0 ?
                        ((studentSummary?.totalPaid || 0) / studentSummary?.totalStudent * 100)?.toFixed(1) : 0}
                      icon={<CurrencyRupee />}
                      color="#2196f3"
                      suffix="%"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                      title="Employee Attendance"
                      value={employeeSummary?.totalEmployee > 0 ?
                        ((employeeSummary?.totalPresentEmp || 0) / employeeSummary?.totalEmployee * 100)?.toFixed(1) : 0}
                      icon={<PeopleIcon />}
                      color="#ff9800"
                      suffix="%"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Footer */}
      <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" align="center">
          Last updated: {dayjs().format('DD MMM YYYY, hh:mm A')} |
          Data as of {dayjs(selectedDate).format('DD MMM YYYY')} |
          {/* Created by: {createdBy} */}
        </Typography>
      </Box>

      {/* Report Dialog */}
      <Dialog open={openReportDialog} onClose={() => setOpenReportDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Report</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Report Type</InputLabel>
            <Select label="Report Type" defaultValue="summary">
              <MenuItem value="summary">Dashboard Summary</MenuItem>
              <MenuItem value="students">Student Report</MenuItem>
              <MenuItem value="fees">Fees Report</MenuItem>
              <MenuItem value="attendance">Attendance Report</MenuItem>
              <MenuItem value="employees">Employee Report</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="date"
            label="From Date"
            sx={{ mt: 2 }}
            slotProps={{ inputLabel: { shrink: true } }}
            defaultValue={selectedDate}
          />
          <TextField
            fullWidth
            type="date"
            label="To Date"
            sx={{ mt: 2 }}
            slotProps={{ inputLabel: { shrink: true } }}
            defaultValue={selectedDate}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Format</InputLabel>
            <Select label="Format" defaultValue="pdf">
              <MenuItem value="pdf">PDF</MenuItem>
              <MenuItem value="excel">Excel</MenuItem>
              <MenuItem value="csv">CSV</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReportDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            setOpenReportDialog(false);
            showSnackbar('Report generation started. You will be notified when ready.', 'success');
          }}>
            Generate Report
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;